import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { moveItem, moveVisible, updateItem, SIZE_LABELS, SIZE_TITLES } from './dashboardLayout';
import './DashboardGrid.css';

const GripIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 3l18 18" /><path d="M10.6 5.1A9.7 9.7 0 0 1 12 5c5 0 8.6 4.4 9.6 6.2a1.6 1.6 0 0 1 0 1.6 16 16 0 0 1-3 3.6" />
    <path d="M6.6 6.6C4.4 8 3 10.2 2.4 11.2a1.6 1.6 0 0 0 0 1.6C3.4 14.6 7 19 12 19c1.9 0 3.6-.6 5-1.5" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

const EDGE = 80;          // зона у края окна, где при перетаскивании страница прокручивается
const SETTLE_MS = 160;    // пауза после перестановки, пока соседи доезжают на место

// Сетка виджетов дашборда. В обычном режиме — просто сетка; в режиме
// настройки у каждого виджета появляется панель: ручка для перетаскивания,
// ширина и «скрыть». Перетаскивание на pointer-событиях, а не на HTML5 DnD:
// тот не работает на телефонах и не даёт прокручивать страницу во время
// перетаскивания, а дашборд длиннее экрана.
const DashboardGrid = ({ layout, widgets, editing, onChange, renderWidget }) => {
  const cellRefs = useRef({});
  const prevRects = useRef({});
  const layoutRef = useRef(layout);
  const drag = useRef(null);
  const ghostRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [announce, setAnnounce] = useState('');

  // Обработчики висят на window всё перетаскивание, поэтому стабильны
  // (без зависимостей) и читают свежие данные через ref.
  const onChangeRef = useRef(onChange);
  const widgetsRef = useRef(widgets);
  layoutRef.current = layout;
  onChangeRef.current = onChange;
  widgetsRef.current = widgets;
  const byId = Object.fromEntries(widgets.map(w => [w.id, w]));
  const visible = layout.filter(item => !item.hidden && byId[item.id]);

  // FLIP: соседи плавно переезжают на новые места, а не перескакивают.
  // Координаты — от начала документа, чтобы автопрокрутка не считалась сдвигом.
  // Анимируем только перестановки внутри режима настройки: при входе в него
  // все ячейки сдвигаются из-за панелей, и «полёт» всей страницы лишь мешает.
  // Скрытая ячейка (виджет вернул null) даёт нулевой прямоугольник в (0, 0) —
  // из него анимировать нельзя, иначе блок прилетает из угла экрана.
  const wasEditing = useRef(editing);
  useLayoutEffect(() => {
    const next = {};
    const animate = editing && wasEditing.current;
    wasEditing.current = editing;
    Object.entries(cellRefs.current).forEach(([id, el]) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) return;
      next[id] = { left: r.left + window.scrollX, top: r.top + window.scrollY };
      const prev = prevRects.current[id];
      if (!animate || !prev || !el.animate) return;
      const dx = prev.left - next[id].left;
      const dy = prev.top - next[id].top;
      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
        el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
          { duration: 180, easing: 'cubic-bezier(.2,.7,.3,1)' });
      }
    });
    prevRects.current = next;
  });

  const findTarget = useCallback((x, y) => {
    const d = drag.current;
    if (!d || Date.now() < d.lockUntil) return;
    const current = layoutRef.current;
    const from = current.findIndex(item => item.id === d.id);
    for (const item of current) {
      if (item.hidden || item.id === d.id) continue;
      const el = cellRefs.current[item.id];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x < r.left || x > r.right || y < r.top || y > r.bottom) continue;
      const to = current.findIndex(i => i.id === item.id);
      // Меняемся местами, только когда указатель прошёл середину цели в
      // сторону движения — иначе соседи разного размера «дрожат» туда-обратно.
      const wide = r.width > el.parentElement.getBoundingClientRect().width * 0.75;
      const past = wide
        ? (to > from ? y > r.top + r.height / 2 : y < r.top + r.height / 2)
        : (to > from ? x > r.left + r.width / 2 || y > r.bottom - r.height / 3
          : x < r.left + r.width / 2 || y < r.top + r.height / 3);
      if (!past) return;
      d.lockUntil = Date.now() + SETTLE_MS;
      onChangeRef.current(moveItem(current, from, to));
      return;
    }
  }, []);

  const autoScroll = useCallback(() => {
    const d = drag.current;
    if (!d) return;
    let speed = 0;
    if (d.y < EDGE) speed = -Math.ceil((EDGE - d.y) / 4);
    else if (d.y > window.innerHeight - EDGE) speed = Math.ceil((d.y - (window.innerHeight - EDGE)) / 4);
    if (speed) window.scrollBy(0, speed);
    // Цель проверяем каждый кадр, а не только на движение указателя: после
    // перестановки (пауза SETTLE_MS) или прокрутки палец может стоять на месте.
    findTarget(d.x, d.y);
    d.raf = requestAnimationFrame(autoScroll);
  }, [findTarget]);

  const moveGhost = (x, y) => {
    if (ghostRef.current) ghostRef.current.style.transform = `translate(${x + 14}px, ${y + 14}px)`;
  };

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    d.x = e.clientX;
    d.y = e.clientY;
    moveGhost(e.clientX, e.clientY);
    findTarget(e.clientX, e.clientY);
  }, [findTarget]);

  const endDrag = useCallback(() => {
    const d = drag.current;
    if (!d) return;
    cancelAnimationFrame(d.raf);
    drag.current = null;
    setDragId(null);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    window.removeEventListener('pointercancel', endDrag);
    const pos = layoutRef.current.filter(i => !i.hidden).findIndex(i => i.id === d.id);
    const title = widgetsRef.current.find(w => w.id === d.id)?.title || d.id;
    setAnnounce(`${title} — позиция ${pos + 1}`);
  }, [onPointerMove]);

  const startDrag = (e, id) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    drag.current = { id, x: e.clientX, y: e.clientY, lockUntil: 0, raf: 0 };
    setDragId(id);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    drag.current.raf = requestAnimationFrame(autoScroll);
    requestAnimationFrame(() => moveGhost(e.clientX, e.clientY));
  };

  useEffect(() => () => {
    // Уход со страницы посреди перетаскивания: снять слушатели окна.
    if (drag.current) cancelAnimationFrame(drag.current.raf);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    window.removeEventListener('pointercancel', endDrag);
  }, [onPointerMove, endDrag]);

  useEffect(() => { if (!editing) endDrag(); }, [editing, endDrag]);

  const onHandleKey = (e, id) => {
    const delta = { ArrowUp: -1, ArrowLeft: -1, ArrowDown: 1, ArrowRight: 1 }[e.key];
    if (!delta) return;
    e.preventDefault();
    const next = moveVisible(layout, id, delta);
    if (next === layout) return;
    onChange(next);
    const pos = next.filter(i => !i.hidden).findIndex(i => i.id === id);
    setAnnounce(`${byId[id].title} — позиция ${pos + 1} из ${visible.length}`);
  };

  return (
    <>
      <div className={`dg-grid ${editing ? 'is-editing' : ''} ${dragId ? 'is-dragging' : ''}`}>
        {visible.map((item, index) => {
          const w = byId[item.id];
          return (
            <section
              key={item.id}
              ref={el => { cellRefs.current[item.id] = el; if (!el) delete cellRefs.current[item.id]; }}
              className={`dg-cell dg-${item.size} ${dragId === item.id ? 'dg-dragged' : ''}`}
              aria-label={editing ? w.title : undefined}
            >
              {editing && (
                <div className="dg-toolbar">
                  <button
                    type="button"
                    className="dg-handle"
                    onPointerDown={(e) => startDrag(e, item.id)}
                    onKeyDown={(e) => onHandleKey(e, item.id)}
                    aria-label={`${w.title}: перетащите или двигайте стрелками. Позиция ${index + 1} из ${visible.length}`}
                    title="Перетащите, чтобы переставить"
                  >
                    <GripIcon />
                    <span className="dg-title">{w.title}</span>
                  </button>
                  {w.sizes.length > 1 && (
                    <div className="dg-sizes" role="group" aria-label="Ширина">
                      {w.sizes.map(size => (
                        <button
                          key={size}
                          type="button"
                          className={`dg-size ${item.size === size ? 'active' : ''}`}
                          aria-pressed={item.size === size}
                          title={SIZE_TITLES[size]}
                          onClick={() => onChange(updateItem(layout, item.id, { size }))}
                        >
                          {SIZE_LABELS[size]}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    className="dg-hide"
                    title="Скрыть виджет"
                    aria-label={`Скрыть «${w.title}»`}
                    onClick={() => onChange(updateItem(layout, item.id, { hidden: true }))}
                  >
                    <EyeOffIcon />
                  </button>
                </div>
              )}
              {/* Обёртка одна и та же в обоих режимах: иначе вход в настройку
                  пересоздавал бы все виджеты и они заново ходили бы за данными. */}
              <div className={editing ? 'dg-content' : 'dg-body'} inert={editing || undefined}>
                {renderWidget(item.id)}
              </div>
            </section>
          );
        })}
      </div>
      {dragId && (
        <div className="dg-ghost" ref={ghostRef} aria-hidden="true">
          <GripIcon /> {byId[dragId]?.title}
        </div>
      )}
      <div className="dg-sr-only" aria-live="polite">{announce}</div>
    </>
  );
};

export default DashboardGrid;
